"use client";

import React, { useState } from "react";
import { ArrowRight, CircleCheck, CircleAlert, ThermometerSnowflake, Factory, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Toggle } from "@/components/ui/toggle";
import { Button } from "@/components/ui/button";
type Project = {
  id: string;
  title: string;
  description: string;
  difficulty: string;
  category: "residential" | "commercial" | "emergency";
  beforeImg: string;
  afterImg: string;
  stats: string[];
};
const projects: Project[] = [{
  id: "res-1",
  title: "Tepelné čerpadlo pro chytrou domácnost",
  description: "Instalace vysoce účinného tepelného čerpadla vzduch-voda pro udržitelnou moderní rezidenci.",
  difficulty: "Precizní instalace",
  category: "residential",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_residential_hvac_old_01.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_residential_hvac_new_01.png",
  stats: ["SCOP 5.1 Rating", "Smart Home Integration"]
}, {
  id: "res-2",
  title: "Split systém pro luxusní vilu",
  description: "Prémiová multi-split klimatizace navržená pro tichý chod v obytných prostorách.",
  difficulty: "Vysoká estetika",
  category: "residential",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_loft_wall_empty_02.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_loft_minisplit_installed_02.png",
  stats: ["A+++ Efficiency", "Nano-Filter Tech"]
}, {
  id: "com-1",
  title: "Průmyslové VRF řešení",
  description: "Velkokapacitní systém s proměnným průtokem chladiva pro vícepodlažní firemní centrálu.",
  difficulty: "Průmyslový rozsah",
  category: "commercial",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_commercial_freezer_iced_03.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_commercial_freezer_fixed_03.png",
  stats: ["Centralized Control", "Energy Recovery"]
}, {
  id: "com-2",
  title: "Klimatizace datového centra",
  description: "Přesné řešení chlazení pro serverová prostředí s vysokou hustotou vyžadující přísnou kontrolu vlhkosti.",
  difficulty: "Kritické systémy",
  category: "commercial",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_server_room_hot_04.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_server_room_cool_04.png",
  stats: ["N+1 Redundancy", "24/7 Monitoring"]
}, {
  id: "emg-1",
  title: "Kritická oprava kompresoru",
  description: "Havarijní výměna vadného průmyslového kompresoru během letních teplotních špiček.",
  difficulty: "Rychlá odezva",
  category: "emergency",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_compressor_broken_05.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_compressor_replaced_05.png",
  stats: ["90-Minute Arrival", "Full Load Restored"]
}, {
  id: "emg-2",
  title: "Stabilizace teploty v laboratoři",
  description: "Urgentní oprava specializované chladicí jednotky pro farmaceutické skladovací prostory.",
  difficulty: "Vysoká naléhavost",
  category: "emergency",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_kitchen_cooler_leak_06.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/project_kitchen_cooler_fixed_06.png",
  stats: ["Zero Stock Loss", "ISO Compliant"]
}];
const ProjectCard = ({
  project,
  index
}: {
  project: Project;
  index: number;
}) => {
  const [showAfter, setShowAfter] = useState(true);
  return <Card data-index={index} className="group overflow-hidden border-border/30 bg-card/40 backdrop-blur-lg transition-all hover:border-primary/60 hover:shadow-2xl hover:shadow-primary/10">
      <div data-section-id="33254" className="relative aspect-video w-full overflow-hidden bg-muted/20">
        {}
        <div className="relative h-full w-full">
          <img src={project.beforeImg} alt={`Before: ${project.title}`} className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out ${showAfter ? "opacity-0" : "opacity-100"}`} />
          <img src={project.afterImg} alt={`After: ${project.title}`} className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out ${showAfter ? "opacity-100" : "opacity-0"}`} />
        </div>

        {}
        <div className="absolute top-0 left-0 right-0 flex items-start justify-between p-4">
          <Badge variant="destructive" className="bg-primary text-primary-foreground shadow-lg font-bold tracking-tight uppercase text-[10px] rounded-sm">
            {project.difficulty}
          </Badge>
          
          <div className="flex items-center gap-2 rounded-full bg-background/80 p-1 shadow-2xl backdrop-blur-md border border-white/10">
            <span className={`text-[10px] font-bold px-2 transition-colors ${!showAfter ? "text-primary" : "text-muted-foreground"}`}>
              BEFORE
            </span>
            <Toggle pressed={showAfter} onPressedChange={setShowAfter} size="sm" className="h-6 w-10 rounded-full data-[state=on]:bg-primary data-[state=on]:text-primary-foreground bg-muted/30 hover:bg-muted-foreground/20 border-transparent" aria-label="Toggle before/after image">
              <div className={`h-4 w-4 rounded-full bg-white shadow-md transition-transform duration-300 ${showAfter ? "translate-x-2" : "-translate-x-2"}`} />
            </Toggle>
            <span className={`text-[10px] font-bold px-2 transition-colors ${showAfter ? "text-primary" : "text-muted-foreground"}`}>
              AFTER
            </span>
          </div>
        </div>
      </div>

      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-bold text-foreground group-hover:text-primary transition-colors tracking-tight">
            {project.title}
          </CardTitle>
          {project.category === "emergency" && <CircleAlert className="h-5 w-5 text-secondary" />}
          {project.category === "residential" && <ThermometerSnowflake className="h-5 w-5 text-primary" />}
          {project.category === "commercial" && <Factory className="h-5 w-5 text-primary" />}
        </div>
        <CardDescription className="text-muted-foreground/80 line-clamp-2 font-medium">
          {project.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="pb-4">
        <div className="flex flex-wrap gap-2">
          {project.stats.map((stat, i) => <div key={i} data-index={i} className="flex items-center gap-1.5 text-xs font-semibold text-foreground/90 bg-muted/40 px-2.5 py-1 rounded-md border border-white/5 font-mono">
              <CircleCheck className="h-3 w-3 text-primary" />
              {stat}
            </div>)}
        </div>
      </CardContent>

      <CardFooter className="pt-0">
        <Button variant="ghost" className="w-full justify-between group/btn hover:bg-primary/10 hover:text-primary border border-white/5 rounded-md" asChild>
          <Link to="/ProjectsPage">
            Zobrazit případovou studii
            <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
          </Link>
        </Button>
      </CardFooter>
    </Card>;
};
export default function ProjectArchive() {
  return <section id="field-work" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-30" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent opacity-30" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex flex-col items-center text-center mb-16 max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/10 px-4 py-1 text-sm uppercase tracking-widest font-bold">
            Portfolio realizací
          </Badge>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-6 text-white uppercase">
            Technická <span className="text-primary">preciznost</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-medium">
            Prozkoumejte náš archiv precizního HVAC inženýrství. Od průmyslových systémů řízení klimatu až po vysoce účinná domácí tepelná čerpadla, NEXO TECH určuje standardy výkonu v terénu.
          </p>
        </div>

        <Tabs defaultValue="residential" className="w-full">
          <div className="flex justify-center mb-12">
            <TabsList className="bg-muted/20 border border-white/10 p-1 h-auto flex-wrap justify-center gap-2 rounded-lg backdrop-blur-md">
              <TabsTrigger value="residential" className="rounded-md px-6 py-2.5 text-sm font-bold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-300 tracking-tight">
                Domácí klimatizace
              </TabsTrigger>
              <TabsTrigger value="commercial" className="rounded-md px-6 py-2.5 text-sm font-bold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-300 tracking-tight">
                Průmyslové systémy
              </TabsTrigger>
              <TabsTrigger value="emergency" className="rounded-md px-6 py-2.5 text-sm font-bold data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground transition-all duration-300 tracking-tight">
                Havarijní podpora
              </TabsTrigger>
            </TabsList>
          </div>

          {}
          <TabsContent value="residential" className="mt-0 outline-none animate-in fade-in slide-in-from-bottom-6 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {projects.filter(p => p.category === "residential").map((project, index) => <ProjectCard key={project.id} project={project} index={index} />)}
            </div>
          </TabsContent>

          {}
          <TabsContent value="commercial" className="mt-0 outline-none animate-in fade-in slide-in-from-bottom-6 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {projects.filter(p => p.category === "commercial").map((project, index) => <ProjectCard key={project.id} project={project} index={index} />)}
            </div>
          </TabsContent>

          {}
          <TabsContent value="emergency" className="mt-0 outline-none animate-in fade-in slide-in-from-bottom-6 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {projects.filter(p => p.category === "emergency").map((project, index) => <ProjectCard key={project.id} project={project} index={index} />)}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-16 text-center">
          <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-10 shadow-xl shadow-primary/20 rounded-md" asChild>
            <Link to="/ContactPage">
              <Wrench className="mr-2 h-4 w-4" />
              Vyžádat technický servis
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}